/**
 * Trial, TaskResult, EvalRunResult — the output shapes of the Task-based
 * eval system. These are the cross-package contracts consumed by:
 *   - eval-harness (produces them in the runner)
 *   - API (stores on Evaluation.scoreSummary)
 *   - console (renders results)
 *   - metrics module (computes pass@k from TaskResult.n and .c)
 *
 * Aligned with the target domain model in DEV-16376 (Eval Tasks Epic).
 *
 * Key distinction:
 *   Trial = one attempt at one Task (single execution)
 *   TaskResult = one Task across all its Trials (aggregated)
 *   EvalRunResult = one Evaluation across all its Tasks (top-level)
 */
import { z } from 'zod';
import { TranscriptMessageSchema } from './transcript.js';
// ─── MetricViolation ────────────────────────────────────────────────────────
/** A tracked metric that exceeded its threshold. */
export const MetricViolationSchema = z.object({
    metric: z.string(),
    value: z.number(),
    threshold: z.number(),
    reason: z.string(),
});
// ─── Trial ──────────────────────────────────────────────────────────────────
/**
 * One attempt at a Task. A Task runs N Trials to measure agent consistency.
 * Each Trial is an independent workflow execution with the same input.
 *
 * The Trial records everything that happened:
 *   - transcript: full ordered message history across all nodes
 *   - outcome: the workflow's final structured output (Run.output)
 *   - graderScores: per-grader normalized score [0, 1]
 *   - graderReasons: per-grader human-readable explanation
 *   - metrics: tracked metric values (latency, turns, tokens)
 *   - metricViolations: thresholded metrics that exceeded limits
 *   - compositeScore: weighted combination per ScoringPolicy
 *   - passed: final verdict per ScoringPolicy
 */
export const TrialSchema = z.object({
    /** Unique Trial identifier. */
    id: z.string(),
    /** Which Task this Trial belongs to. */
    taskId: z.string(),
    /** The workflow Run this Trial executed. */
    runId: z.string(),
    /** 1-indexed Trial number within this Task's execution. */
    trialNumber: z.number().int().positive(),
    /** Full ordered transcript of the Trial (all nodes, all messages). */
    transcript: z.array(TranscriptMessageSchema),
    /** Final structured output from the workflow — NOT the agent's narration.
     *  For recommend-only agents, this is Run.output.
     *  For acting agents, this would be the read-back state (future). */
    outcome: z.unknown(),
    /** Per-grader normalized score in [0, 1]. Keyed by grader name. */
    graderScores: z.record(z.number()),
    /** Per-grader human-readable explanation. Keyed by grader name. */
    graderReasons: z.record(z.string()),
    /** Tracked metric values (latency_ms, n_turns, total_tokens, etc.). */
    metrics: z.record(z.number()),
    /** Thresholded metrics that exceeded their limits. */
    metricViolations: z.array(MetricViolationSchema),
    /** Per-grader metadata for display (type badge, weight, gate flag). Keyed by grader name.
     *  Optional for backwards compatibility with existing stored Trial data. */
    graderMetadata: z
        .record(z.object({
        /** Grader/judge type (e.g. 'CODE', 'LLM_ONE_SHOT'). */
        type: z.string(),
        /** Relative weight used in composite score computation. */
        weight: z.number(),
        /** Whether this grader is a must-pass gate (hybrid scoring mode). */
        gate: z.boolean(),
    }))
        .optional(),
    /** Weighted composite score per the Task's ScoringPolicy. [0, 1]. */
    compositeScore: z.number(),
    /** Final pass/fail verdict per the Task's ScoringPolicy. */
    passed: z.boolean(),
});
// ─── PassAtKMetrics ─────────────────────────────────────────────────────────
/** Per-k reliability metrics using unbiased combinatorial estimators. */
export const PassAtKMetricsSchema = z.object({
    /** P(at least 1 of k sampled trials passes). */
    passAtK: z.number(),
    /** P(all k sampled trials pass). */
    passPowerK: z.number(),
});
// ─── TaskResult ─────────────────────────────────────────────────────────────
/**
 * One Task across all its Trials in a single EvalRun.
 *
 * Source of truth: nTrials (n) and passedTrials (c).
 * All rate metrics are derived — no stored values that can go stale.
 */
export const TaskResultSchema = z.object({
    /** Which Task this result is for. */
    taskId: z.string(),
    /** Task display name. */
    taskName: z.string(),
    /** Total Trials executed (n). */
    nTrials: z.number().int().nonnegative(),
    /** Trials that passed (c). 0 ≤ c ≤ n. */
    passedTrials: z.number().int().nonnegative(),
    /** c / n — the unbiased estimate of the true per-trial success rate. */
    perTrialSuccessRate: z.number(),
    /** Average compositeScore across all Trials. */
    meanCompositeScore: z.number(),
    /** Per-k reliability metrics. Keyed by k value (e.g., { 1: {...}, 3: {...} }).
     *  Populated by the metrics module (computeTaskMetrics). Defaults to {} when
     *  pass@k computation is not yet wired into the runner. */
    metricsByK: z.record(PassAtKMetricsSchema).default({}),
    /** Warnings (e.g., "k=n is degenerate"). */
    warnings: z.array(z.string()).default([]),
    /** Full Trial detail. Omitted in list views for payload size. */
    trials: z.array(TrialSchema).default([]),
});
// ─── RunAggregate ───────────────────────────────────────────────────────────
/** Per-k aggregate across all Tasks in a run. */
export const RunAggregateSchema = z.object({
    /** Fraction of Tasks where perTrialSuccessRate > 0. */
    overallPassRate: z.number(),
    /** Average pass@k across all Tasks. */
    meanPassAtK: z.number(),
    /** Average pass^k across all Tasks. */
    meanPassPowerK: z.number(),
});
// ─── Memory probe summary (DEV-17245) ───────────────────────────────────────
/**
 * Run-level rollup for MEMORY-target evals — the "reported per run" half of
 * DEV-17245. Per-trial attribution lives on each trial's outcome; this block
 * is the aggregate the miss-split decision reads (§6: a dominant write-miss
 * rate points at save guidance, a bad retrieval-miss rate points at ranking)
 * without opening every trial.
 */
export const MemoryEvalSummarySchema = z.object({
    /** MEMORY trials aggregated (trials whose outcome carried attribution). */
    probeTrials: z.number().int().min(0),
    /** Gold entries across those trials, and where each one landed. */
    goldTotal: z.number().int().min(0),
    surfaced: z.number().int().min(0),
    retrievalMiss: z.number().int().min(0),
    writeMiss: z.number().int().min(0),
    compactionLoss: z.number().int().min(0),
    prunedMiss: z.number().int().min(0),
    /** Trials whose probe ran without the vector source (embedding failed or
     *  absent) — retrieval numbers from those trials don't measure the vector
     *  path. */
    vectorInactiveTrials: z.number().int().min(0),
    /**
     * The §6 cost guardrail. Probes are pure retrieval — zero LLM tokens per
     * decision by construction, so this is 0 for the rig itself. It exists as
     * a field (not an implicit) so benchmark-arm summaries can carry a real
     * number through the same shape.
     */
    tokensPerDecision: z.number().min(0),
    /**
     * Explicitly false until principle-judge call tracing lands (PR #1123
     * follow-up): the judge fires one LLM call per agent save on
     * reflection-enabled supervisors and is not counted here — flagged per
     * DEV-17245 rather than silently omitted.
     */
    judgeCostIncluded: z.boolean(),
});
// ─── EvalRunResult ──────────────────────────────────────────────────────────
/**
 * Top-level result for an Evaluation run. Contains per-Task results
 * and per-k aggregates across all Tasks.
 *
 * Stored on Evaluation.scoreSummary (JSONB) after the run completes.
 */
export const EvalRunResultSchema = z.object({
    /** Evaluation ID. */
    evaluationId: z.string(),
    /** Trials per Task for this run. */
    nTrials: z.number().int().positive(),
    /** Reliability horizons used. Default [1]. */
    reportKs: z.array(z.number().int().positive()).default([1]),
    /** Suite targeted (if any). */
    suiteId: z.string().nullable().optional(),
    /** Per-Task results. */
    taskResults: z.array(TaskResultSchema),
    /** Per-k aggregates across all Tasks. Populated by aggregateRunMetrics(). */
    aggregate: z.record(RunAggregateSchema).default({}),
    /** Present only when the run contained MEMORY probe trials (DEV-17245). */
    memory: MemoryEvalSummarySchema.optional(),
});
//# sourceMappingURL=trial.js.map